import { QueryKeys } from "@app/constants/query-keys.ts";
import { useApis } from "@app/data/apis.ts";
import {
    ActiveEnums,
    AddEditPhasePostModel,
    AssignmentDetailPostModel,
    AssignmentExpenseDetailPostModel,
    InsertAssignmentPostModel,
    UpdateAssignmentPhaseActivationPostModel,
    UpdateAssignmentPhaseSortOrderPostModel,
    UpdateBillingPostModel,
    UpdateGeneralPostModel,
    UpdateStatusPostModel,
} from "@app/data/engine.ts";
import { handleApiError } from "@app/helpers/error-helper";
import { formatDate } from "@app/helpers/time-helper.ts";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useQuery } from "bi-lib";
import { MomentInput } from "moment";
import { toast } from "react-toastify";

interface Props {
    id?: number;
    clientId?: number;
    extension?: number;
    activeState?: ActiveEnums;
    searchDesc?: string;
    assignmentIds?: string;
    rosterId?: number;
    rosterDetailId?: number;
    phaseId?: number;
    expenseId?: number;
    assignmentDetailId?: number;
    workPackageId?: number;
    assignmentDt?: MomentInput;
    staffId?: number;
}

export default function useAssignmentData({
    id,
    clientId,
    extension,
    activeState,
    searchDesc,
    assignmentIds,
    rosterId,
    rosterDetailId,
    phaseId,
    expenseId,
    assignmentDetailId,
    workPackageId,
    assignmentDt,
    staffId,
}: Props = {}) {
    const apis = useApis();
    const queryClient = useQueryClient();

    const getCompleteAssignmentQuery = useQuery({
        queryKey: [QueryKeys.CompleteAssignment, { id }],
        queryFn: async () =>
            (await apis.engine.assignment.assignmentGetCompleteAssignmentList({ assignmentId: id })).assignment,
        enabled: id != undefined,
    });

    const getInvoiceAssignmentsListQuery = useQuery({
        queryKey: [QueryKeys.InvoiceAssignmentsList],
        queryFn: () => apis.engine.assignment.assignmentGetInvoiceAssignmentsListList(),
    });

    const getAssignmentsListQuery = useQuery({
        queryKey: [QueryKeys.AssignmentsList, { clientId, extension, activeState, searchDesc }],
        queryFn: () =>
            apis.engine.assignment.assignmentGetAssignmentsListList({
                ClientId: clientId,
                Extension: extension?.toString(),
                ActiveState: activeState,
                SearchDesc: searchDesc,
            }),
    });

    const getAssignmentPhaseListQuery = useQuery({
        queryKey: [QueryKeys.AssignmentsPhaseList, { assignmentIds }],
        queryFn: () =>
            apis.engine.assignment.assignmentPhaseGetAssignmentPhaseListList({
                ids: assignmentIds,
            }),
    });

    const getAssignmentPhaseQuery = useQuery({
        queryKey: [QueryKeys.AssignmentsPhase, { phaseId }],
        queryFn: () =>
            apis.engine.assignment.assignmentPhaseGetAssignmentPhaseList({
                id: phaseId,
            }),
        enabled: phaseId != undefined,
    });

    const getWorkLocationsListQuery = useQuery({
        queryKey: [QueryKeys.AssignmentWorkLocationsList, { id }],
        queryFn: () => apis.engine.assignment.assignmentGetWorkLocationsListList({ assignmentId: id }),
    });

    const getAssignmentRosterListQuery = useQuery({
        queryKey: [QueryKeys.AssignmentRosterList, { id, activeState }],
        queryFn: () => apis.engine.assignment.assignmentGetAssignmentRosterListList({ assignmentId: id, activeState }),
        enabled: id != undefined,
    });

    const getAssignmentRosterDetailListQuery = useQuery({
        queryKey: [QueryKeys.AssignmentRosterDetailList, { rosterId }],
        queryFn: () => apis.engine.assignment.assignmentGetAssignmentRosterDetailListList({ rosterId }),
        enabled: rosterId != undefined,
    });

    const getAssignmentRosterDetailQuery = useQuery({
        queryKey: [QueryKeys.AssignmentRosterDetail, { rosterDetailId }],
        queryFn: () => apis.engine.assignment.assignmentGetRosterDetailList({ rosterDetailId }),
        enabled: rosterDetailId != undefined,
    });

    const getExpenseDetailsQuery = useQuery({
        queryKey: [QueryKeys.AssignmentExpense, { expenseId }],
        queryFn: () => apis.engine.assignment.assignmentExpenseDetailsEntryList({ id: expenseId }),
        enabled: expenseId != undefined,
    });

    const getExpenseDetailsListQuery = useQuery({
        queryKey: [QueryKeys.AssignmentExpenseList, { assignmentId: id }],
        queryFn: () => apis.engine.assignment.assignmentExpenseDetailsGridList({ assignmentId: id }),
        enabled: id != undefined,
    });

    const getAssignmentDetailQuery = useQuery({
        queryKey: [QueryKeys.AssignmentDetail, { assignmentDetailId }],
        queryFn: () => apis.engine.assignment.assignmentDetailEntryList({ id: assignmentDetailId }),
        enabled: assignmentDetailId != undefined,
    });

    const getAssignmentDetailSearchQuery = useQuery({
        queryKey: [QueryKeys.AssignmentDetailSearch, { assignmentDetailId, workPackageId, assignmentDt, staffId }],
        queryFn: () =>
            apis.engine.assignment.assignmentDetailSearchList({
                assignmentId: assignmentDetailId,
                workPackageId,
                assignmentDt: formatDate(assignmentDt!) ?? undefined,
                staffId,
            }),
        enabled: assignmentDetailId != undefined || workPackageId != undefined || assignmentDt != undefined,
    });

    const getAssignmentDetailGridQuery = useQuery({
        queryKey: [QueryKeys.AssignmentDetailGrid, { assignmentId: id }],
        queryFn: () => apis.engine.assignment.assignmentDetailGridList({ assignmentId: id }),
    });

    const getAssignmentInvoiceTypeQuery = useQuery({
        queryKey: [QueryKeys.AssignmentInvoiceType, { id }],
        queryFn: () => apis.engine.assignment.assignmentGetAssignmentInvoiceTypeDetail(id!),
        enabled: id != undefined,
    });

    const wpCodeRequiredQuery = useQuery({
        queryKey: [QueryKeys.AssignmentWpCodeRequired, { id: assignmentDetailId }],
        queryFn: () => apis.engine.assignment.assignmentGetWpCodeRequiredDetail(assignmentDetailId!),
        enabled: assignmentDetailId != undefined,
    });

    const updateGeneralMutation = useMutation({
        mutationFn: (post: UpdateGeneralPostModel) => apis.engine.assignment.assignmentUpdateGeneralCreate(post),
        onSuccess: (_, { assignmentId, defaultWorkLocationCityName }) => {
            void queryClient.invalidateQueries({
                queryKey: [QueryKeys.CompleteAssignment, { id: assignmentId }],
            });
            if (defaultWorkLocationCityName != undefined) {
                void queryClient.invalidateQueries({
                    queryKey: [QueryKeys.GetSortedCityState],
                });
                void queryClient.invalidateQueries({
                    queryKey: [QueryKeys.AssignmentWorkLocationsList],
                });
                void queryClient.invalidateQueries({
                    queryKey: [QueryKeys.WorkLocationList],
                });
            }
            toast.success("Assignment details saved");
        },
        onError: (error: Error) => handleApiError(error, "Failed to save assignment details"),
    });

    const updateBillingMutation = useMutation({
        mutationFn: (post: UpdateBillingPostModel) => apis.engine.assignment.assignmentUpdateBillingCreate(post),
        onSuccess: (_, { assignmentId }) => {
            void queryClient.invalidateQueries({
                queryKey: [QueryKeys.CompleteAssignment, { id: assignmentId }],
            });
            toast.success("Assignment billing details saved");
        },
        onError: (error: Error) => handleApiError(error, "Failed to save assignment billing details"),
    });

    const insertAssignmentMutation = useMutation({
        mutationFn: (post: InsertAssignmentPostModel) => apis.engine.assignment.assignmentInsertAssignmentCreate(post),
        onSuccess: (_, { defaultWorkLocationCityName }) => {
            void queryClient.invalidateQueries({
                queryKey: [QueryKeys.AssignmentsList],
            });
            if (defaultWorkLocationCityName != undefined) {
                void queryClient.invalidateQueries({
                    queryKey: [QueryKeys.GetSortedCityState],
                });
            }
            toast.success("Assignment created");
        },
        onError: (error: Error) => handleApiError(error, "Failed to create assignment"),
    });

    const updateAssignmentPhaseMutation = useMutation({
        mutationFn: (post: AddEditPhasePostModel) =>
            apis.engine.assignment.assignmentPhaseCreateAssignmentPhaseCreate(post),
        onSuccess: (_, { phaseId }) => {
            void queryClient.invalidateQueries({
                queryKey: [QueryKeys.AssignmentsPhaseList],
            });
            void queryClient.invalidateQueries({
                queryKey: [QueryKeys.AssignmentsPhase, { id: phaseId }],
            });
            toast.success("Assignment phase saved");
        },
        onError: (error: Error) => handleApiError(error, "Failed to save assignment phase"),
    });

    const updateAssignmentPhaseActivationMutation = useMutation({
        mutationFn: (post: UpdateAssignmentPhaseActivationPostModel) =>
            apis.engine.assignment.assignmentPhaseUpdateAssignmentPhaseActivationCreate(post),
        onSuccess: (_, { phaseId }) => {
            void queryClient.invalidateQueries({
                queryKey: [QueryKeys.AssignmentsPhaseList],
            });
            void queryClient.invalidateQueries({
                queryKey: [QueryKeys.AssignmentsPhase, { id: phaseId }],
            });
            toast.success("Assignment phase saved");
        },
        onError: (error: Error) => handleApiError(error, "Failed to save assignment phase"),
    });

    const updateExpenseDetailMutation = useMutation({
        mutationFn: (post: AssignmentExpenseDetailPostModel) =>
            apis.engine.assignment.assignmentExpenseDetailsSaveCreate(post),
        onSuccess: (_, { id }) => {
            void queryClient.invalidateQueries({
                queryKey: [QueryKeys.AssignmentExpenseList],
            });
            void queryClient.invalidateQueries({
                queryKey: [QueryKeys.AssignmentExpenseList, { expenseId: id }],
            });
            toast.success("Expense saved");
        },
        onError: (error: Error) => handleApiError(error, "Failed to save expense"),
    });

    const updateAssignmentDetailMutation = useMutation({
        mutationFn: (post: AssignmentDetailPostModel) => apis.engine.assignment.assignmentDetailSaveCreate(post),
        onSuccess: (_, { id }) => {
            void queryClient.invalidateQueries({
                queryKey: [QueryKeys.AssignmentDetailGrid],
            });
            void queryClient.invalidateQueries({
                queryKey: [QueryKeys.AssignmentDetail, { assignmentDetailId: id }],
            });
            toast.success("Assignment detail saved");
        },
        onError: (error: Error) => handleApiError(error, "Failed to save assignment detail"),
    });

    const deleteExpenseDetailMutation = useMutation({
        mutationFn: (id: number) => apis.engine.assignment.assignmentExpenseDetailsDeleteDelete({ id }),
        onSuccess: (_, id) => {
            void queryClient.invalidateQueries({
                queryKey: [QueryKeys.AssignmentExpenseList],
            });
            void queryClient.invalidateQueries({
                queryKey: [QueryKeys.AssignmentExpenseList, { expenseId: id }],
            });
            toast.success("Expense deleted");
        },
        onError: (error: Error) => handleApiError(error, "Failed to delete expense"),
    });

    const deleteAssignmentDetailMutation = useMutation({
        mutationFn: (id: number) => apis.engine.assignment.assignmentDetailDeleteDelete({ id }),
        onSuccess: (_, id) => {
            void queryClient.invalidateQueries({
                queryKey: [QueryKeys.AssignmentsList],
            });
            void queryClient.invalidateQueries({
                queryKey: [QueryKeys.AssignmentDetailGrid],
            });
            void queryClient.invalidateQueries({
                queryKey: [QueryKeys.AssignmentDetail, { expenseId: id }],
            });
            toast.success("Expense deleted");
        },
        onError: (error: Error) => handleApiError(error, "Failed to delete expense"),
    });

    const updateAssignmentStatusMutation = useMutation({
        mutationFn: (post: UpdateStatusPostModel) => apis.engine.assignment.assignmentUpdateStatusPartialUpdate(post),
        onSuccess: (_, { assignmentId }) => {
            void queryClient.invalidateQueries({
                queryKey: [QueryKeys.AssignmentsList],
            });
            void queryClient.invalidateQueries({
                queryKey: [QueryKeys.AssignmentDetailGrid],
            });
            void queryClient.invalidateQueries({
                queryKey: [QueryKeys.AssignmentDetail, { assignmentDetailId: assignmentId }],
            });
            toast.success("Assignment status saved");
        },
        onError: (error: Error) => handleApiError(error, "Failed to save assignment status"),
    });

    const updateAssignmentPhaseSortByMutation = useMutation({
        mutationFn: (post: UpdateAssignmentPhaseSortOrderPostModel) =>
            apis.engine.assignment.assignmentPhaseUpdateAssignmentPhaseSortByCreate(post),
        onSuccess: (_, { phaseId }) => {
            void queryClient.invalidateQueries({
                queryKey: [QueryKeys.AssignmentsPhaseList],
            });
            void queryClient.invalidateQueries({
                queryKey: [QueryKeys.AssignmentsPhase, { id: phaseId }],
            });
            toast.success("Assignment phase saved");
        },
        onError: (error: Error) => handleApiError(error, "Failed to save assignment phase"),
    });

    return {
        completeAssignment: getCompleteAssignmentQuery,
        invoiceAssignmentsList: getInvoiceAssignmentsListQuery,
        assignmentsList: getAssignmentsListQuery,
        assignmentPhaseList: getAssignmentPhaseListQuery,
        assignmentPhase: getAssignmentPhaseQuery,
        workLocationsList: getWorkLocationsListQuery,
        rosterList: getAssignmentRosterListQuery,
        rosterDetailList: getAssignmentRosterDetailListQuery,
        rosterDetail: getAssignmentRosterDetailQuery,
        expenseDetailsList: getExpenseDetailsListQuery,
        expenseDetail: getExpenseDetailsQuery,
        assignmentDetailGrid: getAssignmentDetailGridQuery,
        assignmentDetail: getAssignmentDetailQuery,
        assignmentDetailSearch: getAssignmentDetailSearchQuery,
        assignmentInvoiceType: getAssignmentInvoiceTypeQuery,
        wpCodeRequired: wpCodeRequiredQuery,

        updateGeneral: updateGeneralMutation,
        updateBilling: updateBillingMutation,
        insertAssignment: insertAssignmentMutation,
        updateAssignmentPhase: updateAssignmentPhaseMutation,
        updateAssignmentPhaseActivation: updateAssignmentPhaseActivationMutation,
        updateExpenseDetail: updateExpenseDetailMutation,
        updateAssignmentDetail: updateAssignmentDetailMutation,
        deleteExpenseDetail: deleteExpenseDetailMutation,
        deleteAssignmentDetail: deleteAssignmentDetailMutation,
        updateAssignmentStatus: updateAssignmentStatusMutation,
        updateAssignmentPhaseSortBy: updateAssignmentPhaseSortByMutation,
    };
}
